import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from '@/components/ui/toaster'
import { Toaster as Sonner } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import Layout from './components/Layout'
import Landing from './pages/Landing'
import Index from './pages/Index'
import Checkout from './pages/Checkout'
import Cashier from './pages/Cashier'
import Pricing from './pages/Pricing'
import Reports from './pages/Reports'
import Settings from './pages/Settings'
import SpaceMap from './pages/SpaceMap'
import Subscribers from './pages/Subscribers'
import NotFound from './pages/NotFound'
import Login from './pages/Login'
import Management from './pages/Management'
import { AppProvider } from './store/AppContext'
import { I18nProvider } from './store/I18nContext'
import { AuthProvider, useAuth, UserRole } from './hooks/use-auth'

const ProtectedRoute = ({
  children,
  allowedRoles,
}: {
  children: React.ReactNode
  allowedRoles: UserRole[]
}) => {
  const { profile, loading } = useAuth()

  if (loading)
    return (
      <div className="h-screen flex items-center justify-center">
        Carregando...
      </div>
    )

  if (!profile) return <Navigate to="/login" />

  if (!allowedRoles.includes(profile.role)) {
    if (profile.role === 'financial') return <Navigate to="/reports" />
    if (profile.role === 'operator') return <Navigate to="/dashboard" />
    return <Navigate to="/dashboard" />
  }

  return <>{children}</>
}

const App = () => (
  <AuthProvider>
    <AppProvider>
      <I18nProvider>
        <BrowserRouter
          future={{ v7_startTransition: false, v7_relativeSplatPath: false }}
        >
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route element={<Layout />}>
                <Route
                  path="/management"
                  element={
                    <ProtectedRoute allowedRoles={['ultramaster', 'admin']}>
                      <Management />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute
                      allowedRoles={['ultramaster', 'admin', 'operator']}
                    >
                      <Index />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/checkout"
                  element={
                    <ProtectedRoute
                      allowedRoles={['ultramaster', 'admin', 'operator']}
                    >
                      <Checkout />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/map"
                  element={
                    <ProtectedRoute
                      allowedRoles={['ultramaster', 'admin', 'operator']}
                    >
                      <SpaceMap />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cashier"
                  element={
                    <ProtectedRoute
                      allowedRoles={['ultramaster', 'admin', 'operator']}
                    >
                      <Cashier />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/subscribers"
                  element={
                    <ProtectedRoute
                      allowedRoles={['ultramaster', 'admin', 'operator']}
                    >
                      <Subscribers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/pricing"
                  element={
                    <ProtectedRoute allowedRoles={['ultramaster', 'admin']}>
                      <Pricing />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute
                      allowedRoles={['ultramaster', 'admin', 'financial']}
                    >
                      <Reports />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute allowedRoles={['ultramaster', 'admin']}>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </TooltipProvider>
        </BrowserRouter>
      </I18nProvider>
    </AppProvider>
  </AuthProvider>
)

export default App
